import dayjs from "dayjs";

import { AccessToken } from "@auth/types";
import { AuthSDK } from "@auth/client-sdk";
import { IJsonWebTokenPayload } from "@auth/server-sdk";

import { ExtraCookieOptions, UserIdentifiers } from "./types";

/**
 * Builds cookie settings with consistent domain handling.
 *
 * @param cookieDomain - Domain for the cookie
 * @param expires - Optional `dayjs` instance controlling expiry date
 * @param maxAge - Optional cookie lifespan in seconds
 * @param extraOptions - Additional cookie options like secure, sameSite, httpOnly
 */
export function cookieDomainSettings(
  cookieDomain: string,
  expires?: dayjs.Dayjs | undefined,
  maxAge?: number | undefined,
  extraOptions?: ExtraCookieOptions,
) {
  const secureDefault = cookieDomain !== "localhost";
  const sameSiteDefault = secureDefault ? "none" : "strict";

  return {
    path: "/",
    domain: cookieDomain,
    secure: extraOptions?.secure ?? secureDefault,
    sameSite: extraOptions?.sameSite ?? sameSiteDefault,
    expires: expires?.toDate(),
    maxAge,
    httpOnly: extraOptions?.httpOnly ?? false,
  };
}

/**
 * Builds cookie settings for deleting a cookie.
 *
 * @param name - Name of the cookie to delete
 * @param cookieDomain - Domain for the cookie
 * @returns Cookie settings for deletion
 */
export function cookieDeleteSettings(name: string, cookieDomain: string) {
  return {
    name,
    path: "/",
    domain: cookieDomain,
  };
}
/**
 * Extracts user-identifiable fields from an access token
 *
 * @param accessToken - Raw JWT access token
 * @returns UserIdentifiers or empty object
 */
export function extractIdentifiers(accessToken: AccessToken): UserIdentifiers {
  if (!accessToken) {
    return {};
  }

  try {
    const parsed = AuthSDK.decodePayload(accessToken);

    if (!parsed) {
      return {};
    }

    return {
      email: parsed.email ?? undefined,
      firstName: parsed.firstName ?? undefined,
      lastName: parsed.lastName ?? undefined,
      id: parsed.sub ?? undefined,
      iold: parsed.iold ?? undefined,
      old: parsed.old ?? undefined,
    };
  } catch (err) {

    console.error("Failed to decode access token", err);
    return {};
  }
}

/**
 * Extracts customer ID from JWT payload
 * @param payload - Decoded JWT payload (can be null/undefined)
 * @returns Customer ID or undefined
 */
export function extractCustomerId(payload: IJsonWebTokenPayload | null | undefined): string | undefined {
  return payload?.old ?? payload?.iold;
}
